// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
An antique engagement ring gives you a stone cut by hand for candlelight, a setting nobody else is wearing, and usually more ring for the money than a new one. The trade-offs are that sizing is not always possible, the stone will not match a modern grading report, and condition matters far more than it does with new jewelry.

Here is how to buy one without regretting it.

## Why an old ring is often better value

A new diamond loses most of its retail premium the moment it leaves the shop. An antique ring has already taken that loss, decades ago, and what remains is the value of the materials, the workmanship and the rarity.

You are also buying something that cannot be made again. Nobody is cutting new [old mine or old European cut](/blog/old-mine-cut-vs-old-european-cut-diamonds) diamonds, and the supply of intact period settings only shrinks.

## Know which era you are looking at

Style tells you more than any label, and it drives the price.

| Era | Roughly | What it looks like |
| --- | --- | --- |
| Georgian | before 1837 | Closed backs, foiled stones, silver over gold, rose cuts |
| Victorian | 1837 to 1901 | Yellow gold, clusters, gypsy settings, old mine cuts |
| Edwardian | 1901 to 1915 | Platinum, milgrain, lace-like filigree, old European cuts |
| Art Deco | 1920 to 1935 | Geometry, symmetry, calibre cut coloured stones |
| Retro | 1935 to 1950 | Big yellow and rose gold, bold curves |

Our [era by era dating guide](/blog/how-to-date-antique-jewelry-by-era) goes into the evidence behind each, and the [Art Deco versus Art Nouveau guide](/blog/art-deco-vs-art-nouveau-jewelry) sorts the two most confused style names.

## The stone

**Expect an antique cut.** A small table, a tall crown, and an open culet that reads as a tiny dot through the top. Under warm light these throw big, lazy flashes of colour rather than fine white sparkle. Many people prefer it. Look at one in candlelight or low lamplight before you judge it under a shop's bright spots.

**Do not expect a modern grading report.** Most antique stones have never been to a laboratory, and sending one means unsetting it. Colour and clarity are usually described by the seller's eye. That is normal, and it is why the price is lower.

**Ask about chips.** The culet and the girdle are where a century of wear shows. A small chip hidden under a claw is common and acceptable if it is disclosed and priced in. An undisclosed one is a different conversation.

**Never recut an intact old stone.** Recutting to modern proportions destroys both the character and the collector premium, and loses weight.

If the centre stone is a coloured one, [our gemstone guide](/blog/how-to-tell-if-a-gemstone-is-real) covers natural versus synthetic and which treatments are normal. Synthetic ruby and sapphire in a 1930s ring is period correct, not a red flag.

## The setting, and what to inspect

This is where money is won and lost.

- **Claws.** Look at the tips. Worn, thin or bent claws are the most common repair, and a stone sits in them. Re-tipping is routine but should be reflected in the price.
- **The shank.** Thin at the bottom is normal after a century. Very thin means it needs replacing, which is a real cost and a loss of originality.
- **Evidence of resizing.** A visible seam, a different colour of solder, or a hallmark that has been cut through.
- **Milgrain.** On Edwardian and Deco pieces, the tiny beaded edge softens with wear. Crisp milgrain is a good sign; completely flat milgrain means heavy wear or over-polishing.
- **Original settings versus replaced stones.** Ask directly whether all stones are original. Replaced side stones are common and not a disaster, but they should be stated.

## Size, and the question to ask before anything else

Ask this first, because it can end the conversation: **what size is it, and can it be resized?**

Some rings cannot be. Eternity bands with stones all the way round have no plain metal to cut. Rings with the hallmark on the shank lose the hallmark if that section goes. Carved or engraved shanks lose the pattern. Georgian rings with closed backs and foiled stones cannot take soldering heat safely.

Where resizing is possible, two sizes in either direction is the practical limit before the ring starts to look wrong. Our [guide to measuring ring size at home](/blog/how-to-measure-your-ring-size-at-home) has the method and the conversions, and the mistakes that cause returns.

If it is a surprise proposal, size to the knuckle and accept it may need adjusting afterwards. Or buy a ring that already fits and accept a smaller pool of candidates.

## What to ask the seller

1. What size is it, measured as inside diameter in millimetres?
2. Can it be resized, and has it been resized before?
3. Are all the stones original?
4. Are there any chips, cracks or repairs, and where?
5. What does the hallmark say, and can I see a photograph of it?
6. What does it weigh?
7. Is there a laboratory report, and if not, how was the stone assessed?

A seller who answers "I do not know, but here are clear photographs of the mark and the claws" is being straight with you. Confident answers with no evidence are the ones to slow down on.

## Budget, honestly

The metal and stones give you a floor. Above that you are paying for age, condition, originality and design. Our [resale value guide](/blog/how-much-is-my-jewelry-worth) explains how to work out the floor yourself before you negotiate.

Remember to leave room for what comes after: a possible resize, re-tipping claws, and an insurance valuation. On a hundred-year-old ring, budgeting nothing for immediate work is optimistic.

## Insurance and appraisal

Get the ring valued for insurance after you buy, not before. An insurance valuation states replacement cost, which is much higher than what you paid, and it is the wrong number for judging a purchase but the right one for cover.

Photograph the ring from several angles, including the hallmark and any identifying features, and keep the receipt.

## Buying safely at a distance

Almost all antique rings are bought from a photograph. That is workable if the money is protected and the description is honest.

On Nextmine, the buyer's payment is held by Stripe until the ring has arrived and you have confirmed it, and sellers must ship insured and tracked. If a ring is not as described, you report it within 48 hours and there is a defined process rather than an argument. Read [how payments work](/how-payments-work), or [see what is listed right now](/s).

And if the ring you want is not there, post it on the [ISO board](/wanted). Sellers watch it, and you get an email the moment something matching is listed.

For more on the stones themselves, the Hallmark Society's [guide to old cuts](https://hallmarksociety.com/old-cut-diamonds/) covers mine, European, transitional and rose, and what each is worth against the others.
`;

export default body;
